import { Box, Container, Flex, Text, Heading, Grid } from '@radix-ui/themes';
import { Image } from '@trashlab/sitesapp/src/organizations/denversiteworks/components/shared/image';
import { Button } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/shared/button';
import { IconCheckmark } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/icons/icons';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/denversiteworks/lib/utils/get-variant-value';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/denversiteworks/contexts/use-tenant-context';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

export const LearnMore = () => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);
  const city = orgAddress?.city ?? 'San Francisco';
  
  const learnMoreSectionTitle = useVariantValue(
    'LEARN_MORE_SECTION_TITLE',
    'Why our customers love us',
  );
  
  const learnMoreTitle = useVariantValue(
    'LEARN_MORE_TITLE',
    'Learn more about dumpster company',
  );
  
  const learnMoreSubtitle = useVariantValue(
    'LEARN_MORE_SUBTITLE',
    `At ${
      website?.organization?.name || 'our company'
    }, we're a dedicated team, thriving on
    collaboration, positivity, and a shared passion for what we do. Our
    core mission is to deliver top-tier, affordable, and reliable
    dumpster rental services to ${city} and beyond. We value
    hard work and innovative thinking, and our commitment to going the
    extra mile underpins our approach. We pride ourselves on our
    nationwide-checked team, offering unmatched customer service and
    efficiency.Upholding integrity and continuous learning, we seek to
    exceed client expectations.`,
  );

  const learnMoreFeatures = useVariantValue('LEARN_MORE_FEATURE', [
    {
      value: 'Efficiency',
      orderIndex: 0,
    },
    {
      value: 'Affordability',
      orderIndex: 1,
    },
    {
      value: 'Sustainability',
      orderIndex: 2,
    },
    {
      value: 'Reliability',
      orderIndex: 3,
    },
  ]);

  const learnMoreImage = useVariantValue(
    'LEARN_MORE_IMAGE',
    '/default/why-choose-us.svg',
  );

  const contactUsButtonText = useVariantValue(
    'CONTACT_US_BUTTON_TEXT',
    'Contact us',
  );

  return (
    <Container size="4" p="8">
      <Grid
        columns={{ initial: '1', xs: '2' }}
        width="auto"
        justify="center"
        align="center"
        gap="4"
        className="items-center"
      >
        <Box
          className="flex justify-center items-center"
          data-variable="LEARN_MORE_IMAGE"
        >
          <Image src={learnMoreImage} alt="Learn more" className="rounded-lg" />
        </Box>

        <Flex direction="column" gap="2" className="h-full justify-center">
          <Text size="2" weight="bold" className="text-neutral-400" data-variable="LEARN_MORE_SECTION_TITLE">
            {learnMoreSectionTitle}
          </Text>

          <Heading as="h2" size="8" data-variable="LEARN_MORE_TITLE">
            {learnMoreTitle}
          </Heading>

          <Text
            size="3"
            data-variable="LEARN_MORE_SUBTITLE"
          >
            {learnMoreSubtitle}
          </Text>

          <Flex direction="column" gap="4" mt="4">
            {Array.isArray(learnMoreFeatures) &&
              [...learnMoreFeatures]
                .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                .map((feature) => (
                  <Flex
                    key={`${feature.orderIndex}-${feature.value}`}
                    align="center"
                    gap="4"
                    data-variable="LEARN_MORE_FEATURE"
                    data-order-index={feature.orderIndex}
                  >
                    <IconCheckmark />
                    <Text size="4" weight="medium">
                      {feature.value}
                    </Text>
                  </Flex>
                ))}
          </Flex>

          <Box className="flex items-center col-span-1 mt-4">
            <Button
              href="/contact-us"
              title="Contact our dumpster rental team"
              aria-label="Contact our customer service team to learn more about our services"
              data-variable="CONTACT_US_BUTTON_TEXT"
            >
              {contactUsButtonText}
            </Button>
          </Box>
        </Flex>
      </Grid>
    </Container>
  );
};

export default LearnMore;
